import _ from "lodash";
import React, { memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { CloseCircleFilled } from "@toddle-edu/ds-icons";
import { IconButton, Avatar } from "@toddle-edu/ds-web";

import { useI18n } from "Hooks";
import { getCommentDisplayContent } from "Utils/RegexHelper";
import handleEnter from "Utils/handleEnterKey";
import { isIdEqual } from "Utils/idHelpers";
import { useMessageOptions } from "UIComponents/Message/providers/MessageProvider";
import {
  ATTACHMENT_TYPES,
  getFormattedNameForUserType,
  getIsMessagingWidget,
  areMomentsSame,
  getIsSidePaneMode,
} from "Utils/MessageHelpers";
import { getAvatarColor } from "Utils";
import { AttachmentList } from "Attachment";

import classes from "./ParentMessage.scss";

const mapAttachmentTitleToType = {
  [ATTACHMENT_TYPES.IMAGE]: "common:photo",
  [ATTACHMENT_TYPES.VIDEO]: "common:video",
  [ATTACHMENT_TYPES.FILE]: "common:file",
  [ATTACHMENT_TYPES.LINK]: "common:link",
};
const FILE_LINK_TYPES = [ATTACHMENT_TYPES.FILE, ATTACHMENT_TYPES.LINK];

const style = {
  attachmentContainerWidgetStyle: {
    display: "flex",
    columnGap: "8px",
  },
  containerStyle: {
    flexShrink: "1",
  },
  horizontalCardStyle: {
    maxWidth: "210px",
    height: "32px",
  },
  lineFixHeight: {
    height: "16px",
  },
  lineStretchHight: { alignSelf: "stretch" },
};

const getAttachmentGrid = ({ noOfAttachments }) => {
  return {
    gridTemplateColumns: `repeat(${noOfAttachments} , 200px)`,
    gridGap: "8px",
  };
};

const LabelText = props => {
  const { label, isDeleted, totalAttachments, singleAttachment } = props;
  const { t } = useI18n(["common"]);

  if (isDeleted) return t("common:message_was_deleted");
  if (label) {
    return getCommentDisplayContent(label, {
      getHtml: item => `@${item}`, // without highlighting in parent message
      renderLinksAsAnchor: false,
      newLineReplaceWith: "\n", // keep it as it is
    });
  }

  // If label is not present meaning, this is a attachments only message.

  // In an Ideal scenario, this should never happen, just a fallback
  if (!totalAttachments) return null;
  if (totalAttachments > 1) return t("common:attachments");

  if (
    _.includes(FILE_LINK_TYPES, singleAttachment.type) &&
    singleAttachment.name
  ) {
    return singleAttachment.name;
  }

  const attachmentTitle = _.get(
    mapAttachmentTitleToType,
    singleAttachment.type,
    null
  );

  return attachmentTitle ? t(attachmentTitle) : null;
};

const AttachmentReplyContainerV2 = props => {
  const {
    attachments,
    isParentMessageDeleted,
    isMessagingWidget = false,
    isSidePane = false,
  } = props;
  const noOfAttachments = _.size(attachments);

  if (!noOfAttachments || isParentMessageDeleted) return null;

  const moreAttachmentCountClass = classNames(
    "text-body-2",
    classes.moreAttachmentCount
  );

  //we will only show first 3 attachments rest we will show numbers
  const first3Attachments = _.slice(attachments, 0, 3);

  const getAttachmentContainerStyle = () => {
    if (isMessagingWidget || isSidePane) {
      return style.attachmentContainerWidgetStyle;
    }
    return getAttachmentGrid({ noOfAttachments: _.min([noOfAttachments, 3]) });
  };

  return (
    <div className={classes.attachmentReplyContainer}>
      <AttachmentList
        value={first3Attachments}
        mode="view"
        mediaCardSize={"small"}
        cardType={"design-media-card"}
        mediaCardClasses={classes.mediaCardWrapper}
        isDownloadEnabled={false}
        attachmentContainerStyle={getAttachmentContainerStyle()}
        containerStyle={style.containerStyle}
        horizontalCardStyle={style.horizontalCardStyle}
        showAttachmentAsPost={false}
      />
      {noOfAttachments > 3 ? (
        <div className={moreAttachmentCountClass}>
          {`+${noOfAttachments - 3}`}
        </div>
      ) : null}
    </div>
  );
};

const ReplyContainerV2 = props => {
  const {
    createdBy,
    label,
    isParentMessageDeleted,
    attachments,
    isFromChatBar,
    isParentMessageEdited = false,
  } = props;
  const { t } = useI18n();
  const {
    screenMode,
    isPreviewMessagesModal,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
  } = useMessageOptions();

  const replyToTextClass = classNames("text-label-4", classes.replyToText);
  const deletedMessageClass = classNames(
    "text-label-4",
    classes.deletedMessage
  );
  const isMessagingWidget = getIsMessagingWidget({ screenMode });
  const isSidePane = getIsSidePaneMode({ screenMode });

  const condToWrapMessage =
    (isMessagingWidget || isSidePane) &&
    (isParentMessageDeleted || (_.size(attachments) && !label));

  const upperMessageContainerClass = classNames(classes.upperMessageContainer, {
    [classes.messageContainerWrap]: condToWrapMessage,
  });
  const replyContainerV2Class = classNames(classes.replyContainerV2, {
    [classes.parentMessagePadding]: isFromChatBar,
  });
  const editMessageClass = classNames("text-body-3", classes.editedMessage);
  const labelClasses = classNames(classes.labelClassV2, {
    [classes.labelHoveredV2]: !isPreviewMessagesModal,
  });

  return (
    <div className={replyContainerV2Class}>
      <div
        className={classes.borderLeft}
        style={
          (label && _.size(attachments)) || condToWrapMessage
            ? style.lineStretchHight
            : style.lineFixHeight
        }
      ></div>
      <div className={classes.repliedMessage}>
        <div className={upperMessageContainerClass}>
          <div className={classes.userAvatar}>
            <Avatar
              shade="light"
              size="xxx-small"
              src={_.get(createdBy, "profileImage", null)}
              shape="square"
              color={getAvatarColor(_.get(createdBy, "id", null))}
              name={`${_.get(createdBy, "firstName")} ${_.get(
                createdBy,
                "lastName"
              )}`}
            />
          </div>
          <div className={replyToTextClass}>
            {isFromChatBar
              ? t("common:replying_to_user", {
                  label: getFormattedNameForUserType({
                    user: createdBy,
                    orderBy: defaultNameSortPreference,
                    defaultStudentNameOrderPreference,
                    isFormattedNameV2: true,
                  }),
                })
              : t("common:reply_to_user", {
                  label: getFormattedNameForUserType({
                    user: createdBy,
                    orderBy: defaultNameSortPreference,
                    defaultStudentNameOrderPreference,
                    isFormattedNameV2: true,
                  }),
                })}
          </div>

          {isParentMessageDeleted ? (
            <div className={deletedMessageClass}>
              {`[${t("common:deleted_message")}]`}
            </div>
          ) : label ? (
            <div className={labelClasses}>
              <LabelText label={label} />
            </div>
          ) : (
            <AttachmentReplyContainerV2
              attachments={attachments}
              isParentMessageDeleted={isParentMessageDeleted}
              isMessagingWidget={isMessagingWidget}
              isSidePane={isSidePane}
            />
          )}

          {isParentMessageEdited && !isParentMessageDeleted ? (
            <div className={editMessageClass}>{`(${t(
              "integration:edited"
            )})`}</div>
          ) : null}
        </div>
        {label && _.size(attachments) && !isParentMessageDeleted ? (
          <div className={classes.replyMessageBottomContainer}>
            <AttachmentReplyContainerV2
              attachments={attachments}
              isMessagingWidget={isMessagingWidget}
              isSidePane={isSidePane}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ParentMessage = props => {
  const {
    userId,
    parentMessage,
    goToParentMessage,
    isClosable,
    onCloseClick,
    isFromChatBar = false,
    isPreviewMessagesModal,
  } = props;

  const {
    label,
    createdBy,
    attachments,
    isDeleted: isParentMessageDeleted = false,
    createdAt: parentMessageCreatedAt,
    updatedAt: parentMessageUpdatedAt,
  } = parentMessage;

  const isParentMessageEdited =
    !!parentMessageUpdatedAt &&
    !areMomentsSame({
      date1: parentMessageCreatedAt,
      date2: parentMessageUpdatedAt,
    });

  const totalAttachments = _.size(attachments);

  const isParentMessageSentByUser = isIdEqual(_.get(createdBy, "id"), userId);

  const onClickMessage = e => {
    e.stopPropagation();
    goToParentMessage(parentMessage);
  };

  const handleCloseClick = e => {
    e.stopPropagation();
    onCloseClick(e);
  };

  return (
    <div
      tabIndex={isPreviewMessagesModal ? undefined : 0}
      role={isPreviewMessagesModal ? undefined : "button"}
      onClick={isPreviewMessagesModal ? undefined : onClickMessage}
      onKeyDown={
        isPreviewMessagesModal ? undefined : handleEnter(onClickMessage)
      }
      className={classes.containerV2}
    >
      <ReplyContainerV2
        createdBy={createdBy}
        label={label}
        isParentMessageSentByUser={isParentMessageSentByUser}
        isParentMessageDeleted={isParentMessageDeleted}
        totalAttachments={totalAttachments}
        attachments={attachments}
        isFromChatBar={isFromChatBar}
        isParentMessageEdited={isParentMessageEdited}
      />

      {isClosable ? (
        <IconButton
          size="medium"
          variant="plain"
          className={classes.closeIcon}
          onClick={handleCloseClick}
          icon={<CloseCircleFilled />}
        />
      ) : null}
    </div>
  );
};

ParentMessage.propTypes = {
  goToParentMessage: PropTypes.func.isRequired,
  isChildMessageSentByUser: PropTypes.bool.isRequired,
  parentMessage: PropTypes.object.isRequired,
  defaultNameSortPreference: PropTypes.string.isRequired,
  isClosable: PropTypes.bool,
  isPlainContainer: PropTypes.bool,
  onCloseClick: PropTypes.func,
};

ParentMessage.defaultProps = {
  isPlainContainer: false,
  isClosable: false,
  isFromChatBar: false,
  onCloseClick: _.noop,
};

export default memo(ParentMessage);
